import React, { ElementType, useEffect, useMemo } from 'react';

import { IDynamicText } from '@/types/contentful';

import { trackWithFingerprint } from '../../utils/trackWithFingerprint';

import { usePageDataContext } from '../../context/PageData';
import { addAdditionalDataToDynamicText } from '../../utils/addAdditionalDataToDynamicText';

interface ITextWithOptions extends React.HTMLAttributes<HTMLElement> {
  as?: ElementType;
  className?: string;
  style?: React.CSSProperties;
  keyword: string;
  channel?: string;
  adGroupId?: string;
  fallback: string;
  options?: IDynamicText[];
  module?: string;
}

export function TextWithOptions({
  as: Tag = 'p',
  className,
  style,
  keyword,
  channel,
  adGroupId,
  fallback,
  options,
  module,
  ...props
}: ITextWithOptions) {
  const { advocateUserData, query } = usePageDataContext();
  const value = useMemo(() => {
    const variation = options?.find(
      ({ fields }: IDynamicText) =>
        fields.keyword === keyword &&
        (!adGroupId ||
          (fields.adgroupId && fields.adgroupId.includes(adGroupId))) &&
        fields.channels.includes(channel as string)
    );

    return addAdditionalDataToDynamicText(
      variation?.fields?.text,
      variation?.fields?.keyGeneratedText,
      advocateUserData,
      fallback,
      query
    );
  }, [keyword, channel, options]);

  useEffect(() => {
    if (keyword && channel !== 'organic' && options?.length && !value) {
      trackWithFingerprint('Dynamic Text Failed', {
        module,
        keyword,
        channel,
        allowedOptions: options?.map(({ fields }) => ({
          keyword: fields?.keyword,
          channels: fields?.channels
        }))
      });
    }
  }, [keyword, channel, options, value]);

  return (
    <Tag className={className} style={style} {...props}>
      {value ?? fallback}
    </Tag>
  );
}
